import React from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { I18nProvider } from '@/contexts/I18nContext';
import { ThemeProvider } from '@/contexts/ThemeContext';

// Layouts
import PublicLayout from '@/components/layouts/PublicLayout';
import DashboardLayout from '@/components/layouts/DashboardLayout';

// Public pages
import Landing from '@/pages/Landing';
import Login from '@/pages/Login';
import Register from '@/pages/Register';
import AuthCallback from '@/pages/AuthCallback';
import VerifyCard from '@/pages/VerifyCard';

// Citizen pages
import CitizenDashboard from '@/pages/citizen/Dashboard';
import NewApplication from '@/pages/citizen/NewApplication';
import MyDocuments from '@/pages/citizen/MyDocuments';
import TrackStatus from '@/pages/citizen/TrackStatus';
import MyCard from '@/pages/citizen/MyCard';
import Notifications from '@/pages/citizen/Notifications';
import Profile from '@/pages/citizen/Profile';
import Appointments from '@/pages/citizen/Appointments';

// Embassy pages
import EmbassyDashboard from '@/pages/embassy/EmbassyDashboard';
import ApplicationList from '@/pages/embassy/ApplicationList';
import ApplicationDetail from '@/pages/embassy/ApplicationDetail';
import EmbassySchedule from '@/pages/embassy/EmbassySchedule';
import EmbassyAppointments from '@/pages/embassy/EmbassyAppointments';

// Admin pages
import AdminDashboard from '@/pages/admin/AdminDashboard';
import ManageEmbassies from '@/pages/admin/ManageEmbassies';
import ManageUsers from '@/pages/admin/ManageUsers';
import CmsSettings from '@/pages/admin/CmsSettings';
import AuditLogs from '@/pages/admin/AuditLogs';
import Analytics from '@/pages/admin/Analytics';
import SecurityAdmin from '@/pages/admin/SecurityAdmin';

function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-[#007FFF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) {
    if (user.role === 'admin') return <Navigate to="/admin/dashboard" replace />;
    if (user.role === 'embassy') return <Navigate to="/embassy/dashboard" replace />;
    return <Navigate to="/citizen/dashboard" replace />;
  }

  return children;
}

function AppRouter() {
  return (
    <Routes>
      {/* Public routes */}
      <Route element={<PublicLayout />}>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/verify" element={<VerifyCard />} />
        <Route path="/verify/:cardNumber" element={<VerifyCard />} />
      </Route>

      {/* Google OAuth callback */}
      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* Redirect /dashboard based on role */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <RoleRedirect />
        </ProtectedRoute>
      } />

      {/* Citizen routes */}
      <Route element={<ProtectedRoute roles={['citizen']}><DashboardLayout /></ProtectedRoute>}>
        <Route path="/citizen/dashboard" element={<CitizenDashboard />} />
        <Route path="/citizen/new-application" element={<NewApplication />} />
        <Route path="/citizen/documents" element={<MyDocuments />} />
        <Route path="/citizen/track-status" element={<TrackStatus />} />
        <Route path="/citizen/my-card" element={<MyCard />} />
        <Route path="/citizen/notifications" element={<Notifications />} />
        <Route path="/citizen/profile" element={<Profile />} />
        <Route path="/citizen/appointments" element={<Appointments />} />
      </Route>

      {/* Embassy routes */}
      <Route element={<ProtectedRoute roles={['embassy']}><DashboardLayout /></ProtectedRoute>}>
        <Route path="/embassy/dashboard" element={<EmbassyDashboard />} />
        <Route path="/embassy/applications" element={<ApplicationList />} />
        <Route path="/embassy/applications/:appId" element={<ApplicationDetail />} />
        <Route path="/embassy/schedule" element={<EmbassySchedule />} />
        <Route path="/embassy/appointments" element={<EmbassyAppointments />} />
      </Route>

      {/* Admin routes */}
      <Route element={<ProtectedRoute roles={['admin']}><DashboardLayout /></ProtectedRoute>}>
        <Route path="/admin/dashboard" element={<AdminDashboard />} />
        <Route path="/admin/embassies" element={<ManageEmbassies />} />
        <Route path="/admin/users" element={<ManageUsers />} />
        <Route path="/admin/cms" element={<CmsSettings />} />
        <Route path="/admin/analytics" element={<Analytics />} />
        <Route path="/admin/security" element={<SecurityAdmin />} />
        <Route path="/admin/audit-logs" element={<AuditLogs />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function RoleRedirect() {
  const { user } = useAuth();
  if (user?.role === 'admin') return <Navigate to="/admin/dashboard" replace />;
  if (user?.role === 'embassy') return <Navigate to="/embassy/dashboard" replace />;
  return <Navigate to="/citizen/dashboard" replace />;
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <I18nProvider>
          <AuthProvider>
            <AppRouter />
            <Toaster position="top-right" richColors />
          </AuthProvider>
        </I18nProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
